<template>
  <Modal v-if="visible" :TogglePopup="close">
    <article class="sim-details" aria-labelledby="sim-details-title">
      <header class="sim-details-header">
        <div>
          <p>Trackers SIM management</p>
          <h1 id="sim-details-title">IMEI: {{ value(sim.imei) }}</h1>
          <span>{{ value(sim.phoneNumber) }}</span>
        </div>
        <button type="button" class="btn sim-details-sms-button" :disabled="!sim.phoneNumber" @click="showSmsComposer = true">Send SMS</button>
      </header>

      <section class="sim-details-section">
        <h2>Device details</h2>
        <dl class="sim-details-grid">
          <template v-for="field in simFields" :key="field.label">
            <dt>{{ field.label }}</dt>
            <dd v-if="field.key === 'notes'">
              <div v-if="editingNotes" class="sim-notes-edit">
                <input v-model="notesDraft" type="text" class="form-control" aria-label="Notes" @keydown.enter="saveNotes" @keydown.escape="cancelNotes">
                <button type="button" class="btn btn-sm sim-notes-save" :disabled="savingNotes" @click="saveNotes">Save</button>
                <button type="button" class="btn btn-sm sim-notes-cancel" :disabled="savingNotes" @click="cancelNotes">Cancel</button>
              </div>
              <div v-else class="sim-notes-value">
                <span>{{ value(field.value) }}</span>
                <button type="button" class="btn sim-details-icon-button" title="Edit notes" aria-label="Edit notes" @click="startEditingNotes"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true"><path d="M13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001zm-.644.766a.5.5 0 0 0-.707 0L1.95 11.756l-.764 3.057 3.057-.764L14.44 3.854a.5.5 0 0 0 0-.708l-1.585-1.585z"/></svg></button>
              </div>
              <p v-if="notesError" class="sim-details-error sim-notes-error">{{ notesError }}</p>
            </dd>
            <dd v-else><span :class="field.className">{{ value(field.value) }}</span></dd>
          </template>
        </dl>
        <p v-if="loadingTracker" class="sim-details-muted sim-details-loading">Loading AppEngine details…</p>
        <p v-else-if="trackerError" class="sim-details-error sim-details-loading">{{ trackerError }}</p>
      </section>

      <section class="sim-details-section">
        <h2>Users info</h2>
        <p v-if="loadingOwner" class="sim-details-muted">Loading owner details…</p>
        <p v-else-if="ownerError" class="sim-details-error">{{ ownerError }}</p>
        <dl class="sim-details-grid">
          <dt>Owner</dt>
          <dd class="sim-user-info-value">
            <span>{{ ownerSummary }}</span>
            <button
              type="button"
              class="btn sim-details-icon-button"
              title="Open owner details"
              aria-label="Open owner details"
              :disabled="!ownerPhone || loadingOwner"
              @click="$emit('open-user', ownerDetailsUser)"
            ><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="bi bi-info-lg" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14z"/><path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588z"/><circle cx="8" cy="4.5" r="1"/></svg></button>
          </dd>
          <dt>Team</dt>
          <dd class="sim-user-info-value">
            <span>{{ value(teamDisplay || sim.pricingModel) }}</span>
            <button
              type="button"
              class="btn sim-details-icon-button"
              title="Open team details"
              aria-label="Open team details"
              :disabled="!(teamDisplay || sim.pricingModel)"
              @click="$emit('open-team', teamDisplay || sim.pricingModel)"
            ><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="bi bi-info-lg" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14z"/><path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588z"/><circle cx="8" cy="4.5" r="1"/></svg></button>
          </dd>
          <dt>Shared with users</dt>
          <dd class="sim-shared-users">
            <template v-if="sharedUsers.length">
              <div v-for="sharedUser in sharedUsers" :key="sharedUser.phoneNumber" class="sim-shared-user">
                <span>{{ sharedUser.summary }}</span>
                <button
                  v-if="sharedUser.user"
                  type="button"
                  class="btn sim-details-icon-button"
                  title="Open user details"
                  :aria-label="`Open details for ${sharedUser.phoneNumber}`"
                  @click="$emit('open-user', sharedUser.user)"
                ><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="bi bi-info-lg" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14z"/><path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588z"/><circle cx="8" cy="4.5" r="1"/></svg></button>
              </div>
            </template>
            <span v-else>{{ loadingSharedUsers ? 'Loading shared users…' : '–' }}</span>
          </dd>
        </dl>
      </section>

      <section class="sim-details-section">
        <div class="sim-details-section-heading">
          <h2>Latest device information</h2>
          <button
            type="button"
            class="btn sim-details-refresh"
            :disabled="loadingLastInfo || !sim.imei"
            title="Refresh latest device information"
            @click="refreshLastInfo"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 1 0-.908-.418A6 6 0 1 0 8 2v1z"/><path d="M8 0a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-.5.5H5a.5.5 0 0 1 0-1h2.5V.5A.5.5 0 0 1 8 0z"/></svg>
            {{ loadingLastInfo ? 'Refreshing…' : 'Refresh' }}
          </button>
        </div>
        <p v-if="loadingLastInfo" class="sim-details-muted">Loading latest device information…</p>
        <p v-else-if="lastInfoError" class="sim-details-error">{{ lastInfoError }}</p>
        <template v-else>
          <div class="sim-last-info-layout">
            <div>
              <dl class="sim-details-grid">
                <template v-for="field in lastInfoTelemetryFields" :key="field.label">
                  <dt>{{ field.label }}</dt>
                  <dd>{{ value(field.value) }}</dd>
                </template>
              </dl>

            </div>

            <aside class="sim-last-info-location">
              <h3>Last location</h3>
              <dl class="sim-details-grid">
                <template v-for="field in lastInfoLocationFields" :key="field.label">
                  <dt>{{ field.label }}</dt>
                  <dd>{{ value(field.value) }}</dd>
                </template>
              </dl>

              <div v-if="googleMapsHybridUrl" class="sim-location-map">
                <iframe
                  :src="googleMapsHybridUrl"
                  title="Device's last location in Google Maps"
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                />
              </div>
              <p v-else class="sim-details-muted sim-location-empty">Last location coordinates are unavailable.</p>
            </aside>
          </div>

          <div v-if="deviceMessages.length" class="sim-details-messages">
            <h3>Messages sent by the device</h3>
            <div class="sim-details-table-wrap">
              <table class="table table-sm sim-details-table">
                <thead><tr><th>Time</th><th>Type</th><th>Message</th></tr></thead>
                <tbody>
                  <tr v-for="(message, index) in deviceMessages" :key="`${message.createdEpoch}-${index}`">
                    <td>{{ formatTimestamp(message.createdEpoch) }}</td>
                    <td>{{ value(message.type) }}</td>
                    <td>{{ value(message.message) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="cellPhoneHistory.length" class="sim-details-messages">
            <h3>SMS commands sent from phone</h3>
            <div class="sim-details-table-wrap">
              <table class="table table-sm sim-details-table">
                <thead><tr><th>Time</th><th>Phone</th><th>Action</th></tr></thead>
                <tbody>
                  <tr v-for="(entry, index) in cellPhoneHistory" :key="`${entry.createdEpoch}-${index}`">
                    <td>{{ formatTimestamp(entry.createdEpoch) }}</td>
                    <td>{{ value(entry.cellPhone) }}</td>
                    <td>{{ value(entry.action) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </section>
    </article>
  </Modal>

  <Modal v-if="showSmsComposer" :TogglePopup="closeSmsComposer">
    <div class="sms-dialog"><SmsComposer :recipient="String(sim.phoneNumber || '')" type="device-command" /></div>
  </Modal>
</template>

<script>
import axios from 'axios'
import Modal from '@/components/Modal.vue'
import SmsComposer from '@/components/SmsComposer.vue'
import { API_ENDPOINTS } from '@/config/api'

export default {
  name: 'SimDetailsModal',
  components: { Modal, SmsComposer },
  props: {
    visible: { type: Boolean, default: false },
    sim: { type: Object, default: () => ({}) },
    teamDisplay: { type: String, default: '' },
    togglePopup: { type: Function, required: true }
  },
  emits: ['open-user', 'open-team'],
  data() {
    return {
      tracker: {},
      owner: {},
      ownerPhone: '',
      sharedUsersByPhone: {},
      loadingTracker: false,
      loadingOwner: false,
      loadingSharedUsers: false,
      lastInfo: {},
      loadingLastInfo: false,
      editingNotes: false,
      savingNotes: false,
      notesDraft: '',
      trackerError: '',
      ownerError: '',
      lastInfoError: '',
      notesError: '',
      showSmsComposer: false,
      detailsRequestId: 0,
      lastInfoRequestId: 0,
      sharedUsersRequestId: 0
    }
  },
  computed: {
    visibleSimKey() {
      if (!this.visible) return ''
      return [this.sim?.id, this.sim?.imei, this.sim?.phoneNumber]
        .map((value) => String(value || '').trim())
        .join('|')
    },
    simFields() {
      return [
        { label: 'Valid until', value: this.formatDateOnly(this.sim.validUntil), className: this.getValidUntilClass(this.sim.validUntil) },
        { label: 'Deleted', value: this.sim.deleted },
        { label: 'Status', value: this.sim.status },
        { label: 'Operator', value: this.sim.operator },
        { label: 'Brand', value: this.sim.brand, className: this.getBrandClass(this.sim.brand) },
        { key: 'notes', label: 'Notes', value: this.sim.notes },
        { label: 'Role', value: this.tracker.role },
        { label: 'Tracker name', value: this.tracker.name }
      ]
    },
    sharedPhoneNumbers() {
      const value = this.tracker.sharedWith || this.tracker.sharedPhones || this.tracker.shared
      const phoneNumbers = Array.isArray(value)
        ? value.map((item) => item?.phoneNumber || item?.cellNumber || item?.cellnumber || item)
        : (value == null || String(value).trim() === '' ? [] : String(value).split(','))
      const ownerPhoneKey = this.normalizePhoneNumber(this.ownerPhone)
      const seen = new Set()
      return phoneNumbers
        .map((phoneNumber) => String(phoneNumber || '').trim())
        .filter((phoneNumber) => {
          const key = this.normalizePhoneNumber(phoneNumber)
          if (!key || key === ownerPhoneKey || seen.has(key)) return false
          seen.add(key)
          return true
        })
    },
    sharedUsers() {
      return this.sharedPhoneNumbers.map((phoneNumber) => {
        const user = this.sharedUsersByPhone[this.normalizePhoneNumber(phoneNumber)]
        return { phoneNumber, user, summary: user ? this.formatUserSummary(user, phoneNumber) : phoneNumber }
      })
    },
    ownerDetailsUser() {
      return { ...this.owner, cellnumber: this.owner?.cellnumber || this.owner?.phoneNumber || this.ownerPhone }
    },
    ownerSummary() {
      return this.formatUserSummary(this.owner, this.ownerPhone)
    },
    lastInfoTelemetryFields() {
      return [
        { label: 'Latest device information', value: this.formatTimestamp(this.lastInfo.lastInfoUpdatedEpoch) },
        { label: 'Device status', value: this.lastInfo.deviceMode },
        { label: 'GSM signal', value: this.formatGsmSignal() },
        { label: 'Mobile network', value: this.formatNetworkOperator() },
        { label: 'GNSS', value: this.formatGnss() },
        { label: 'Firmware version', value: this.lastInfo.fwVersion },
        { label: 'Battery voltage', value: this.formatVoltage() },
        { label: 'Charging', value: this.lastInfo.charging == null ? null : (this.lastInfo.charging ? 'Yes' : 'No') },
        { label: 'Speed', value: this.lastInfo.deviceSpeed == null ? null : `${this.lastInfo.deviceSpeed} m/s` },
        { label: 'Activity', value: this.lastInfo.accelerometer == null ? null : `${this.lastInfo.accelerometer} g` }
      ]
    },
    lastInfoLocationFields() {
      return [
        { label: 'Last location signal time', value: this.formatTimestamp(this.lastInfo.lastLocation?.createdEpoch) },
        { label: 'Last location coordinates', value: this.formatCoordinates(this.lastInfo.lastLocation) }
      ]
    },
    locationCoordinates() {
      const location = this.lastInfo.lastLocation || {}
      if (location.latitude == null || location.longitude == null) return null

      const latitude = Number(location.latitude)
      const longitude = Number(location.longitude)
      return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null
    },
    googleMapsHybridUrl() {
      if (!this.locationCoordinates) return ''
      const { latitude, longitude } = this.locationCoordinates
      const query = encodeURIComponent(`${latitude},${longitude}`)
      return `https://maps.google.com/maps?hl=et&q=${query}&t=h&z=16&output=embed`
    },
    deviceMessages() {
      return Array.isArray(this.lastInfo.deviceMessageHistory) ? this.lastInfo.deviceMessageHistory : []
    },
    cellPhoneHistory() {
      return Array.isArray(this.lastInfo.cellPhoneHistory) ? this.lastInfo.cellPhoneHistory : []
    }
  },
  watch: {
    visibleSimKey: {
      immediate: true,
      handler(simKey) {
        if (simKey) {
          this.loadDetails()
        } else {
          this.showSmsComposer = false
          this.invalidatePendingRequests()
        }
      }
    }
  },
  methods: {
    close() {
      this.togglePopup()
    },
    closeSmsComposer() {
      this.showSmsComposer = false
    },
    value(fieldValue) {
      return fieldValue == null || String(fieldValue).trim() === '' ? '–' : fieldValue
    },
    formatTimestamp(timestamp) {
      const date = new Date(Number(timestamp))
      return Number.isNaN(date.getTime()) || !timestamp
        ? null
        : new Intl.DateTimeFormat('et-EE', { dateStyle: 'short', timeStyle: 'medium' }).format(date)
    },
    formatDateOnly(dateValue) {
      if (dateValue == null || String(dateValue).trim() === '') return null
      const match = String(dateValue).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
      return match ? `${match[3]}.${match[2]}.${match[1]}` : dateValue
    },
    getValidUntilClass(validUntil) {
      if (validUntil == null || String(validUntil).trim() === '') return ''
      const match = String(validUntil).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!match) return ''

      const validDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999)
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)
      const expiresSoonEnd = new Date(todayStart)
      expiresSoonEnd.setDate(expiresSoonEnd.getDate() + 14)
      expiresSoonEnd.setHours(23, 59, 59, 999)

      if (validDate < todayStart) return 'sim-valid-expired'
      if (validDate <= expiresSoonEnd) return 'sim-valid-soon'
      return ''
    },
    getBrandClass(brand) {
      const normalizedBrand = String(brand || '').trim()
      if (normalizedBrand === 'Huntloc') return 'sim-brand-huntloc'
      if (normalizedBrand === 'Doglo') return 'sim-brand-doglo'
      return ''
    },
    normalizePhoneNumber(phoneNumber) {
      return String(phoneNumber || '').replace(/\D/g, '')
    },
    formatUserSummary(user, fallbackPhoneNumber = '') {
      const name = String(user?.name || user?.username || '').trim()
      const phoneNumber = String(user?.cellnumber || user?.phoneNumber || user?.cellNumber || fallbackPhoneNumber || '').trim()
      const email = String(user?.email || '').trim()
      return [name, phoneNumber, email].filter(Boolean).join(', ') || '–'
    },
    isCurrentDetailsRequest(requestId, imei) {
      return this.visible
        && requestId === this.detailsRequestId
        && String(this.sim?.imei || '').trim() === imei
    },
    isCurrentLastInfoRequest(requestId, imei) {
      return this.visible
        && requestId === this.lastInfoRequestId
        && String(this.sim?.imei || '').trim() === imei
    },
    invalidatePendingRequests() {
      this.detailsRequestId += 1
      this.lastInfoRequestId += 1
      this.sharedUsersRequestId += 1
      this.loadingTracker = false
      this.loadingOwner = false
      this.loadingSharedUsers = false
      this.loadingLastInfo = false
    },
    startEditingNotes() {
      this.notesDraft = this.sim.notes == null ? '' : String(this.sim.notes)
      this.notesError = ''
      this.editingNotes = true
    },
    cancelNotes() {
      if (this.savingNotes) return
      this.editingNotes = false
      this.notesError = ''
    },
    async saveNotes() {
      if (this.savingNotes) return
      this.savingNotes = true
      this.notesError = ''
      try {
        const payload = { ...this.sim, notes: this.notesDraft }
        delete payload.pricingModelSelection
        await axios.put(`${API_ENDPOINTS.TIW_API}/sim`, payload)
        this.sim.notes = this.notesDraft
        this.editingNotes = false
      } catch (_) {
        this.notesError = 'Unable to save notes.'
      } finally {
        this.savingNotes = false
      }
    },
    async loadSharedUsers(detailsRequestId, imei) {
      const phoneNumbers = this.sharedPhoneNumbers
      const requestId = ++this.sharedUsersRequestId
      this.sharedUsersByPhone = {}
      if (!phoneNumbers.length) return

      this.loadingSharedUsers = true
      try {
        const users = await Promise.all(phoneNumbers.map(async (phoneNumber) => {
          try {
            const response = await axios.get(`${API_ENDPOINTS.TIW_API}/users/${encodeURIComponent(phoneNumber)}`)
            const user = response?.data
            return user ? {
              key: this.normalizePhoneNumber(phoneNumber),
              user: {
                ...user,
                cellnumber: user.cellnumber || user.phoneNumber || user.cellNumber || phoneNumber
              }
            } : null
          } catch (_) {
            return null
          }
        }))
        if (!this.isCurrentDetailsRequest(detailsRequestId, imei) || requestId !== this.sharedUsersRequestId) return

        this.sharedUsersByPhone = users.filter(Boolean).reduce((result, entry) => {
          result[entry.key] = entry.user
          return result
        }, {})
      } finally {
        if (requestId === this.sharedUsersRequestId) this.loadingSharedUsers = false
      }
    },
    formatCoordinates(location) {
      if (location?.latitude == null || location?.longitude == null) return null
      const latitude = Number(location.latitude)
      const longitude = Number(location.longitude)
      return Number.isFinite(latitude) && Number.isFinite(longitude)
        ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
        : null
    },
    formatGsmSignal() {
      const strength = Number(this.lastInfo.gsmSignalStrength)
      return Number.isFinite(strength) ? `${strength} / 32` : null
    },
    formatNetworkOperator() {
      const countries = { 248: 'Estonia', 247: 'Latvia', 246: 'Lithuania', 244: 'Finland' }
      const operators = { 1: 'Telia', 2: 'Elisa', 3: 'Tele2' }
      const country = countries[this.lastInfo.countryCode] || ''
      const operator = operators[this.lastInfo.gsmOperator] || ''
      return [country, operator].filter(Boolean).join(', ') || null
    },
    formatGnss() {
      const satelliteCount = Number(this.lastInfo.gnssSatelliteCount)
      const info = String(this.lastInfo.gnssSatInfo || '').trim()
      if (!info && !Number.isFinite(satelliteCount)) return null
      return `${info || '–'}${Number.isFinite(satelliteCount) ? ` (${satelliteCount} satellites)` : ''}`
    },
    formatVoltage() {
      if (this.lastInfo.batteryVoltage == null || this.lastInfo.batteryVoltage === '') return null
      const voltage = Number(this.lastInfo.batteryVoltage)
      if (!Number.isFinite(voltage)) return null

      // Same conversion used by the Doglo Flutter and Huntloc Android apps.
      const rawPercent = 130 - (130 / Math.pow(1 + Math.pow(voltage / 3.55, 50), 0.165))
      const percent = Math.min(100, Math.round(rawPercent))
      return `${voltage.toFixed(2)} V (${percent}%)`
    },
    async loadLastInfo(imei) {
      const requestId = ++this.lastInfoRequestId
      this.loadingLastInfo = true
      this.lastInfoError = ''
      try {
        const isLocalDevelopment = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
        const apiBase = isLocalDevelopment ? '/api' : API_ENDPOINTS.TIW_API
        const response = await axios.get(`${apiBase}/tracker/${encodeURIComponent(imei)}/last-info`, {
          params: { _: Date.now() }
        })
        if (!this.isCurrentLastInfoRequest(requestId, imei)) return
        this.lastInfo = response?.data || {}
      } catch (_) {
        if (!this.isCurrentLastInfoRequest(requestId, imei)) return
        this.lastInfoError = 'Unable to load the latest device information.'
      } finally {
        if (requestId === this.lastInfoRequestId) this.loadingLastInfo = false
      }
    },
    refreshLastInfo() {
      const imei = String(this.sim?.imei || '').trim()
      if (imei && !this.loadingLastInfo) this.loadLastInfo(imei)
    },
    async loadDetails() {
      const imei = String(this.sim?.imei || '').trim()
      const requestId = ++this.detailsRequestId
      this.lastInfoRequestId += 1
      this.sharedUsersRequestId += 1
      this.tracker = {}
      this.owner = {}
      this.ownerPhone = ''
      this.sharedUsersByPhone = {}
      this.loadingTracker = false
      this.loadingOwner = false
      this.loadingSharedUsers = false
      this.loadingLastInfo = false
      this.lastInfo = {}
      this.editingNotes = false
      this.savingNotes = false
      this.notesDraft = this.sim.notes == null ? '' : String(this.sim.notes)
      this.trackerError = ''
      this.ownerError = ''
      this.lastInfoError = ''
      this.notesError = ''

      if (!imei) {
        this.trackerError = 'This SIM has no IMEI, so AppEngine details cannot be loaded.'
        this.ownerError = 'This SIM has no IMEI, so the owner cannot be found.'
        return
      }

      this.loadLastInfo(imei)

      this.loadingTracker = true
      try {
        const response = await axios.get(`${API_ENDPOINTS.TIW_API}/tracker/${encodeURIComponent(imei)}`)
        if (!this.isCurrentDetailsRequest(requestId, imei)) return
        this.tracker = response?.data || {}
        this.ownerPhone = String(this.tracker.ownerPhone || '').trim()
        this.loadSharedUsers(requestId, imei)
      } catch (_) {
        if (!this.isCurrentDetailsRequest(requestId, imei)) return
        this.trackerError = 'Unable to load AppEngine device details.'
      } finally {
        if (requestId === this.detailsRequestId) this.loadingTracker = false
      }

      if (!this.isCurrentDetailsRequest(requestId, imei)) return
      if (!this.ownerPhone) {
        this.ownerError = 'No owner is assigned to this device.'
        return
      }

      this.loadingOwner = true
      try {
        const response = await axios.get(`${API_ENDPOINTS.TIW_API}/users/${encodeURIComponent(this.ownerPhone)}`)
        if (!this.isCurrentDetailsRequest(requestId, imei)) return
        this.owner = {
          ...(response?.data || {}),
          cellnumber: response?.data?.cellnumber || response?.data?.phoneNumber || this.ownerPhone
        }
      } catch (error) {
        if (!this.isCurrentDetailsRequest(requestId, imei)) return
        this.ownerError = error?.response?.status === 404
          ? 'Owner user was not found.'
          : 'Unable to load owner details.'
      } finally {
        if (requestId === this.detailsRequestId) this.loadingOwner = false
      }
    }
  }
}
</script>

<style scoped>
.sim-details { width: min(94vw, 860px); padding: 4px; color: #202629; }
.sim-details-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px; padding: 20px; border-left: 4px solid #f56600; border-radius: 9px; background: #202629; color: #fff; }
.sim-details-header p { margin: 0 0 4px; color: #c8d0d3; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.sim-details-header h1 { margin: 0; font-size: 22px; font-weight: 700; }
.sim-details-header span { display: block; margin-top: 8px; font-size: 13px; }
.sim-details-sms-button, .user-details-sms-button { flex: 0 0 auto; border-color: #f56600; background: #f56600; color: #fff; font-size: 12px; }
.sim-details-sms-button:hover:not(:disabled), .user-details-sms-button:hover:not(:disabled) { border-color: #d95700; background: #d95700; color: #fff; }
.sms-dialog { width: min(94vw, 600px); padding: 4px; }
.sim-details-section { margin-bottom: 12px; padding: 16px; border: 1px solid #e1e5e6; border-radius: 9px; background: #fbfcfc; }
.sim-details-section h2 { margin: 0 0 14px; font-size: 15px; font-weight: 700; }
.sim-details-section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.sim-details-section-heading h2 { flex: 1 1 auto; }
.sim-details-refresh { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 5px; margin: -2px 0 0; border: 1px solid #d7dfe2; background: #fff; color: #40545e; font-size: 12px; }
.sim-details-refresh:hover:not(:disabled) { border-color: #f56600; color: #d95700; }
.sim-details-grid { display: grid; grid-template-columns: minmax(120px, 180px) minmax(0, 1fr); gap: 8px 16px; margin: 0; font-size: 13px; }
.sim-details-grid dt { color: #687277; font-weight: 700; }
.sim-details-grid dd { min-width: 0; margin: 0; overflow-wrap: anywhere; }
.sim-details-grid a { color: #d95700; }
.sim-valid-expired, .sim-valid-soon, .sim-brand-huntloc, .sim-brand-doglo { padding: 2px 6px; }
.sim-valid-expired { background-color: salmon; color: #000; }
.sim-valid-soon { background-color: #ffe066; color: #000; }
.sim-brand-huntloc { background-color: #ff9a00; color: #000; }
.sim-brand-doglo { background-color: #000; color: #fff; }
.sim-user-info-value, .sim-shared-user { display: flex; align-items: baseline; gap: 7px; }
.sim-shared-user + .sim-shared-user { margin-top: 6px; }
.sim-details-icon-button { padding: 0; border: 0; color: #d95700; line-height: 1; }
.sim-details-icon-button:hover:not(:disabled) { color: #a94200; }
.sim-details-icon-button:disabled { color: #aeb8bc; opacity: .65; }
.sim-notes-value { display: flex; align-items: baseline; gap: 7px; }
.sim-notes-edit { display: flex; align-items: center; gap: 6px; }
.sim-notes-edit .form-control { flex: 1 1 auto; width: auto; min-width: 0; height: 30px; font-size: 13px; }
.sim-notes-save, .sim-notes-cancel { flex: 0 0 auto; padding: 6px 10px; white-space: nowrap; font-size: 12px; }
.sim-notes-save { border-color: #f56600; background: #f56600; color: #fff; }
.sim-notes-cancel { border-color: #d7dfe2; background: #fff; color: #40545e; }
.sim-notes-error { margin-top: 5px; }
.sim-details-muted, .sim-details-error { margin: 0; font-size: 13px; }
.sim-details-muted { color: #687277; }
.sim-details-error { color: #b42318; }
.sim-details-loading { margin-top: 14px; }
.sim-details-owner-link { margin-top: 14px; border: 1px solid #f56600; background: #f56600; color: #fff; font-size: 12px; }
.sim-details-owner-link:hover:not(:disabled) { border-color: #d95700; background: #d95700; color: #fff; }
.sim-last-info-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(280px, 0.9fr); gap: 20px; align-items: start; }
.sim-last-info-location { min-width: 0; padding-left: 20px; border-left: 1px solid #e1e5e6; }
.sim-last-info-location h3 { margin: 0 0 12px; font-size: 13px; font-weight: 700; }
.sim-location-map { height: 280px; margin-top: 16px; overflow: hidden; border: 1px solid #d7dddf; border-radius: 7px; background: #eef1f2; }
.sim-location-map iframe { display: block; width: 100%; height: 100%; border: 0; }
.sim-location-empty { margin-top: 16px; }
.sim-details-messages { margin-top: 18px; }
.sim-details-messages h3 { margin: 0 0 8px; font-size: 13px; font-weight: 700; }
.sim-details-table-wrap { overflow-x: auto; border: 1px solid #e1e5e6; border-radius: 6px; background: #fff; }
.sim-details-table { min-width: 560px; margin: 0; font-size: 12px; }
.sim-details-table th, .sim-details-table td { padding: 8px; vertical-align: top; border-color: #e1e5e6; overflow-wrap: anywhere; }
.sim-details-table th { color: #40545e; font-weight: 700; }
@media (max-width: 600px) {
  .sim-details { width: calc(100vw - 24px); }
  .sim-details-header, .sim-details-section { padding: 14px; }
  .sim-last-info-layout { grid-template-columns: 1fr; }
  .sim-last-info-location { padding: 16px 0 0; border-top: 1px solid #e1e5e6; border-left: 0; }
  .sim-details-grid { grid-template-columns: 1fr; gap: 2px; }
  .sim-details-grid dt { margin-top: 8px; }
}
</style>
